import { useEffect, useRef } from 'react'
import styles from './WaveformMicrofono.module.css'

interface Props {
  /** Volume normalizzato 0..1 — di solito da AnalyserNode */
  volume: number
  /** True quando l'utente parla (verde), false quando il lead parla (grigio) */
  attivo: boolean
  /** Numero di barre verticali */
  barre?: number
}

/**
 * Visualizzazione SVG delle barre verticali del microfono.
 * Animate via requestAnimationFrame con effetto random per simulare
 * il cambio di intensità in tempo reale anche quando il volume è statico.
 */
export function WaveformMicrofono({ volume, attivo, barre = 32 }: Props) {
  const refs = useRef<(SVGRectElement | null)[]>([])
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    let phase = 0
    const tick = () => {
      phase += 0.1
      refs.current.forEach((rect, i) => {
        if (!rect) return
        // Variazione sinusoidale + rumore casuale, modulata dal volume
        const variazione = (Math.sin(phase + i * 0.4) + 1) / 2 // 0..1
        const rumore = Math.random() * 0.3
        const intensita = attivo ? Math.min(1, volume * 1.5 + 0.1) : 0.05
        const h = 4 + intensita * (variazione * 0.6 + rumore * 0.4) * 44
        const y = (48 - h) / 2
        rect.setAttribute('height', `${h}`)
        rect.setAttribute('y', `${y}`)
      })
      rafRef.current = requestAnimationFrame(tick)
    }
    tick()
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [volume, attivo])

  const larghezza = barre * 6
  return (
    <svg
      className={styles.svg}
      viewBox={`0 0 ${larghezza} 48`}
      preserveAspectRatio="xMidYMid meet"
    >
      {Array.from({ length: barre }, (_, i) => (
        <rect
          key={i}
          ref={(el) => { refs.current[i] = el }}
          x={i * 6}
          y={22}
          width={3}
          height={4}
          rx={1.5}
          fill={attivo ? 'var(--accent)' : 'var(--text-muted)'}
        />
      ))}
    </svg>
  )
}
